import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Sparkles, ArrowRight, Target, Palette, PenLine, Rocket, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import RefinementSection from '../components/RefinementSection';
import EmotionCheckpoint from '../components/EmotionCheckpoint';

// ============ AI PROFILE REVELATION ============
// The complete brand profile experience
// Premium, feminine, emotionally intelligent

const LOADING_STAGES = [
  { text: "Analyse de ton profil...", duration: 4000 },
  { text: "Création de ton identité de marque...", duration: 6000 },
  { text: "Définition de ta stratégie...", duration: 6000 },
  { text: "Finalisation de ton profil...", duration: null }
];

const ProfileLoading = ({ loadingStage }) => (
  <div className="min-h-screen bg-[#030303] flex items-center justify-center">
    <div className="text-center max-w-md px-6">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative w-24 h-24 mx-auto mb-12"
      >
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 border border-[#D4AF37]/20 rounded-full"
        />
        <div className="absolute inset-4 border border-[#D4AF37]/40 rounded-full flex items-center justify-center bg-[#D4AF37]/5">
          <Crown className="text-[#D4AF37]" size={28} />
        </div>
      </motion.div>
      
      <div className="flex justify-center gap-2 mb-8">
        {LOADING_STAGES.map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: index <= loadingStage ? 1 : 0.3 }}
            className={`w-8 h-0.5 rounded-full transition-all duration-500 ${
              index <= loadingStage ? 'bg-[#D4AF37]' : 'bg-[#27272A]'
            }`}
          />
        ))}
      </div>
      
      <AnimatePresence mode="wait">
        <motion.p
          key={loadingStage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.5 }}
          className="text-white text-lg mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {LOADING_STAGES[loadingStage]?.text}
        </motion.p>
      </AnimatePresence>
      
      <p className="text-[#52525B] text-xs tracking-wide">
        Ton profil de marque prend forme
      </p>
    </div>
  </div>
);

const ProfileRevelation = () => {
  const [stage, setStage] = useState(0); // 0: loading, 1-5: stages, 6: signature/refinement, 7: emotion checkpoint, 8: standalone refinement
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState(0);
  const [error, setError] = useState(null);
  const [isRefining, setIsRefining] = useState(false);
  const [refinedProfile, setRefinedProfile] = useState(null);
  const [showRefinementMode, setShowRefinementMode] = useState(false);
  const [showEmotionCheckpoint, setShowEmotionCheckpoint] = useState(false);
  
  const { token, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Determine if already completed (after auth loads) - but allow refinement mode
  const isAlreadyCompleted = !authLoading && user?.profile_revelation_completed === true && !showRefinementMode;

  // Progressive loading (only if not already completed)
  useEffect(() => {
    if (!isLoading || isAlreadyCompleted || authLoading) return;
    
    const timers = [];
    let currentTime = 0;
    
    LOADING_STAGES.forEach((s, index) => {
      if (s.duration && index < LOADING_STAGES.length - 1) {
        currentTime += s.duration;
        const timer = setTimeout(() => setLoadingStage(index + 1), currentTime);
        timers.push(timer);
      }
    });
    
    return () => timers.forEach(t => clearTimeout(t));
  }, [isLoading]);

  // Generate profile on mount (only if not already completed)
  useEffect(() => {
    if (isAlreadyCompleted || authLoading) return;
    
    const generateProfile = async () => {
      try {
        const response = await axios.post(
          `${API}/onboarding/generate-profile`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProfile(response.data);
        setIsLoading(false);
        setTimeout(() => setStage(1), 800);
      } catch (err) {
        console.error('Profile generation error:', err);
        setError('Une erreur est survenue. Veuillez réessayer.');
        setIsLoading(false);
      }
    };

    generateProfile();
  }, [token, isAlreadyCompleted]);

  const nextStage = () => setStage(prev => prev + 1);
  
  const goToEmotionCheckpoint = () => {
    setShowEmotionCheckpoint(true);
  };
  
  const goToVisionRevelation = () => navigate('/vision-revelation');
  
  const goToRefinement = () => {
    setShowRefinementMode(true); // Enable refinement mode to bypass "already completed" screen
  };
  
  // Refinement handler
  const handleRefine = async (notes) => {
    setIsRefining(true);
    try {
      const response = await axios.post(
        `${API}/profile/refine`,
        { refinement_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefinedProfile(response.data);
      setProfile(response.data); // Update main profile with refined version
    } catch (err) {
      console.error('Profile refinement error:', err);
    }
    setIsRefining(false);
  };
  
  const continueJourney = () => {
    // Navigate to the next incomplete step
    if (!user?.vision_revelation_completed) {
      navigate('/vision-revelation');
    } else if (!user?.content_revelation_completed) {
      navigate('/content-revelation');
    } else if (!user?.first_action_completed) {
      navigate('/first-action');
    } else {
      navigate('/dashboard');
    }
  };

  // Continue journey but show emotion checkpoint first if not given
  const continueWithCheckpoint = () => {
    // Check if user already gave feedback for profile step
    // If not, show the emotion checkpoint first
    setShowEmotionCheckpoint(true);
  };
  
  // Retry profile generation
  const retryGeneration = () => {
    setError(null);
    setIsLoading(true);
    setLoadingStage(0);
  };

  // Show loading while auth is loading
  if (authLoading) return <ProfileLoading loadingStage={0} />;
  
  // Show standalone refinement screen when user clicked "Affiner ma vision"
  if (showRefinementMode) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <AnimatePresence mode="wait">
          <motion.div
            key="standalone-refinement"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <RefinementSection
              stepName="profile"
              isRefining={isRefining}
              refinedData={refinedProfile}
              onRefine={handleRefine}
              onContinue={goToVisionRevelation}
              onSkip={() => setShowRefinementMode(false)}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }
  
  // Show "already completed" screen if step is done
  if (isAlreadyCompleted) {
    // Show Emotion Checkpoint if triggered
    if (showEmotionCheckpoint) {
      return (
        <EmotionCheckpoint
          step="profile"
          onComplete={continueJourney}
        />
      );
    }
    
    return (
      <div className="min-h-screen bg-[#030303]">
        <AnimatePresence mode="wait">
          <motion.div
            key="already-completed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="w-20 h-20 mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
            >
              <Crown className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mb-6"
            >
              Étape complétée
            </motion.p>
            
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-white text-2xl md:text-4xl text-center mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ton profil a déjà été révélé.
            </motion.h2>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-[#71717A] text-base text-center max-w-md mb-12"
            >
              Tu as déjà découvert ton profil de marque.
              <br />
              Que souhaites-tu faire maintenant ?
            </motion.p>
            
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="flex flex-col items-center gap-4"
            >
              <Button
                onClick={continueWithCheckpoint}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-10 py-6 text-base font-medium"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="continue-journey"
              >
                Continuer mon parcours <ArrowRight className="ml-3" size={18} />
              </Button>
              
              <Button
                onClick={goToRefinement}
                className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-5"
                data-testid="refine-profile"
              >
                <Sparkles className="mr-2" size={16} />
                Affiner ma vision
              </Button>
              
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-transparent text-[#71717A] hover:text-white px-8 py-3"
                data-testid="go-dashboard"
              >
                Accéder à mon espace
              </Button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  if (isLoading) return <ProfileLoading loadingStage={loadingStage} />;

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 mx-auto mb-6 border border-red-400/30 rounded-full flex items-center justify-center">
            <Crown className="text-red-400" size={24} />
          </div>
          <p className="text-white text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Oups, une erreur s'est produite
          </p>
          <p className="text-[#A1A1AA] text-sm mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <Button 
              onClick={retryGeneration} 
              className="bg-[#D4AF37] hover:bg-[#B8860B] text-black w-full"
              data-testid="profile-retry-btn"
            >
              Réessayer <ArrowRight className="ml-2" size={16} />
            </Button>
            <button 
              onClick={() => navigate('/onboarding')}
              className="text-[#71717A] text-sm hover:text-white transition-colors"
            >
              Retourner à l'onboarding
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] overflow-hidden">
      <AnimatePresence mode="wait">
        
        {/* Stage 1: Intro */}
        {stage === 1 && (
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="w-20 h-20 mb-10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center"
            >
              <Crown className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-6"
            >
              Ton profil de marque
            </motion.p>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="text-white text-3xl md:text-5xl text-center max-w-2xl leading-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Voici qui tu es.
            </motion.h1>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="text-[#A1A1AA] text-base text-center max-w-md"
            >
              Un profil créé uniquement pour toi.
              <br />Basé sur tout ce que tu nous as confié.
            </motion.p>
            
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.5 }}
              className="mt-12"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-10 py-6 text-base"
                data-testid="profile-continue"
              >
                Découvrir <ArrowRight className="ml-3" size={18} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* Stage 2: Identity Summary */}
        {stage === 2 && (
          <motion.div
            key="identity"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.p
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-8"
            >
              Ton identité
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="max-w-3xl text-center"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {profile?.identity_summary}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.5 }}
              className="mt-14"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-8 py-5"
                data-testid="profile-next-2"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* Stage 3: Core Axes */}
        {stage === 3 && (
          <motion.div
            key="axes"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.div
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-3 mb-10"
            >
              <Target className="text-[#D4AF37]" size={18} />
              <p className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase">
                Tes axes prioritaires
              </p>
            </motion.div>
            
            <div className="max-w-3xl w-full space-y-6">
              {profile?.core_axes?.map((axis, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + (index * 0.2), duration: 0.5 }}
                  className="border border-[#27272A] p-6 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[#D4AF37] text-xs tracking-[0.15em]">0{index + 1}</span>
                    <h3 
                      className="text-white text-lg"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {axis?.axis}
                    </h3>
                  </div>
                  <p className="text-[#A1A1AA] text-sm mb-2">{axis?.description}</p>
                  <p className="text-[#D4AF37]/70 text-xs italic">Focus: {axis?.focus}</p>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="mt-10"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-8 py-5"
                data-testid="profile-next-3"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* Stage 4: Brand Positioning + Visual */}
        {stage === 4 && (
          <motion.div
            key="positioning"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.div
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-3 mb-10"
            >
              <Palette className="text-[#D4AF37]" size={18} />
              <p className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase">
                Ton positionnement
              </p>
            </motion.div>
            
            <div className="max-w-2xl w-full grid gap-8">
              {/* Energy */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <p className="text-[#52525B] text-xs tracking-[0.15em] uppercase mb-2">Ton énergie</p>
                <p className="text-white text-base leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {profile?.brand_positioning?.energy}
                </p>
              </motion.div>
              
              {/* Perception */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <p className="text-[#52525B] text-xs tracking-[0.15em] uppercase mb-2">Comment tu dois être perçue</p>
                <p className="text-white text-base leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {profile?.brand_positioning?.perception}
                </p>
              </motion.div>
              
              {/* Visual Direction */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="border-t border-[#1C1C1C] pt-6"
              >
                <p className="text-[#52525B] text-xs tracking-[0.15em] uppercase mb-2">Ta direction visuelle</p>
                <p className="text-[#D4AF37] text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {profile?.visual_direction?.style}
                </p>
                <p className="text-[#A1A1AA] text-sm">{profile?.visual_direction?.aesthetic}</p>
              </motion.div>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="mt-10"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-8 py-5"
                data-testid="profile-next-4"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* Stage 5: Action Plan */}
        {stage === 5 && (
          <motion.div
            key="action"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.div
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-3 mb-10"
            >
              <Rocket className="text-[#D4AF37]" size={18} />
              <p className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase">
                Tes 3 premières actions
              </p>
            </motion.div>
            
            <div className="max-w-2xl w-full space-y-6">
              {profile?.action_plan?.map((action, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + (index * 0.2), duration: 0.5 }}
                  className="flex gap-4"
                >
                  <div className="w-10 h-10 flex-shrink-0 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                    <span className="text-[#D4AF37] text-sm font-medium">{action?.step}</span>
                  </div>
                  <div>
                    <p className="text-white text-base mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {action?.action}
                    </p>
                    <p className="text-[#71717A] text-sm">{action?.why}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="mt-10"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-8 py-5"
                data-testid="profile-next-5"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* Stage 6: Signature + Final */}
        {stage === 6 && (
          <motion.div
            key="signature"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.p
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-6"
            >
              Ta signature
            </motion.p>
            
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="max-w-3xl text-center py-8"
            >
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="w-20 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mb-8"
              />
              
              <p 
                className="text-[#D4AF37] text-2xl md:text-3xl leading-relaxed italic"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                "{profile?.signature_phrase}"
              </p>
              
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1.5, duration: 0.6 }}
                className="w-20 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mt-8"
              />
            </motion.div>
            
            {/* Refinement Section - Co-creation feature */}
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 2, duration: 0.5 }}
              className="w-full"
            >
              <RefinementSection
                stepName="profile"
                isRefining={isRefining}
                refinedData={refinedProfile}
                onRefine={handleRefine}
                onContinue={goToEmotionCheckpoint}
                onSkip={goToEmotionCheckpoint}
              />
            </motion.div>
          </motion.div>
        )}

        {/* Stage 7: Emotion Checkpoint */}
        {showEmotionCheckpoint && (
          <EmotionCheckpoint
            step="profile"
            onComplete={goToVisionRevelation}
          />
        )}
        
      </AnimatePresence>
    </div>
  );
};

export default ProfileRevelation;
